import { useRef, useState } from 'react';
import { ArrowRight, Heart, MessageCircle, Send, Bookmark, MoreHorizontal, Copy, Play } from 'lucide-react';
import { resolveImageUrl } from '@/contexts/BrandingContext';

const IG_URL = 'https://www.instagram.com/estadia365/';
const IG_GREEN = '#2DBE7A';
const IG_ICON_PATH = "M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z";

function InstagramIcon({ className }) {
  return <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d={IG_ICON_PATH} /></svg>;
}

function FeedCard({ post, username, avatarUrl }) {
  const [imgFailed, setImgFailed] = useState(false);
  const key = post.code || post.post_id;
  return (
    <a href={post.instagram_url || IG_URL} target="_blank" rel="noopener noreferrer"
      className="group snap-center shrink-0 w-[82%] sm:w-[60%] md:w-auto rounded-2xl overflow-hidden bg-white dark:bg-[#141D2E] border border-[#E7E5E4] dark:border-white/10 shadow-sm hover:shadow-2xl hover:-translate-y-1 transition-[transform,box-shadow] duration-300"
      title={post.caption || ''} data-testid={`ig-feed-post-${key}`}>
      <div className="flex items-center gap-3 px-4 py-3">
        <span className="w-9 h-9 rounded-full bg-white ring-1 ring-black/10 dark:ring-white/15 overflow-hidden flex items-center justify-center shrink-0">
          {avatarUrl ? <img src={avatarUrl} alt={username} className="w-full h-full object-cover" /> : <InstagramIcon className="w-5 h-5 text-[#E4405F]" />}
        </span>
        <span className="text-sm font-semibold text-[#1C1917] dark:text-white" data-testid={`ig-feed-username-${key}`}>{username}</span>
        <MoreHorizontal className="w-5 h-5 ml-auto text-[#78716C] dark:text-white/70" />
      </div>
      <div className="relative aspect-square overflow-hidden bg-[#F5F4F0] dark:bg-[#0F1520]">
        {!imgFailed && post.thumbnail_url ? (
          <img src={resolveImageUrl(post.thumbnail_url)} alt={post.caption ? post.caption.slice(0, 120) : 'Instagram'} loading="lazy"
            className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500" onError={() => setImgFailed(true)} />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#833AB4] via-[#E4405F] to-[#FCAF45]">
            <InstagramIcon className="w-14 h-14 text-white/70" />
          </div>
        )}
        {post.media_type === 'carousel' && <Copy className="absolute top-3 right-3 w-5 h-5 text-white drop-shadow-md" />}
        {post.media_type === 'video' && <Play className="absolute top-3 right-3 w-5 h-5 text-white fill-white drop-shadow-md" />}
      </div>
      <div className="flex items-center gap-4 px-4 py-3 text-[#1C1917] dark:text-white">
        <Heart className="w-6 h-6 transition-colors duration-200 group-hover:text-[#E4405F] group-hover:fill-[#E4405F]" />
        <MessageCircle className="w-6 h-6 -scale-x-100" />
        <Send className="w-6 h-6" />
        <Bookmark className="w-6 h-6 ml-auto" />
      </div>
    </a>
  );
}

function FeedCarousel({ posts, username, avatarUrl }) {
  const trackRef = useRef(null);
  const [active, setActive] = useState(0);
  const onScroll = () => {
    const el = trackRef.current;
    if (!el || !posts.length) return;
    setActive(Math.min(posts.length - 1, Math.round(el.scrollLeft / (el.scrollWidth / posts.length))));
  };
  const goTo = (i) => {
    const el = trackRef.current;
    const child = el?.children[i];
    if (!el || !child) return;
    el.scrollTo({ left: child.offsetLeft - el.offsetLeft - (el.clientWidth - child.clientWidth) / 2, behavior: 'smooth' });
  };
  return (
    <div className="min-w-0">
      <div ref={trackRef} onScroll={onScroll}
        className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 md:grid md:grid-cols-3 md:gap-5 md:overflow-visible md:pb-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        data-testid="ig-feed-track">
        {posts.map((post) => <FeedCard key={post.post_id || post.code} post={post} username={username} avatarUrl={avatarUrl} />)}
      </div>
      <div className="flex justify-center gap-2 mt-6" data-testid="ig-feed-dots">
        {posts.map((post, i) => (
          <button key={post.post_id || post.code} type="button" onClick={() => goTo(i)} aria-label={`Post ${i + 1}`}
            className={`h-2 rounded-full transition-[width,background-color] duration-300 ${i === active ? 'w-2 bg-[#2DBE7A]' : 'w-2 bg-[#D6D3D1] dark:bg-white/25'}`}
            data-testid={`ig-feed-dot-${i}`} />
        ))}
      </div>
    </div>
  );
}

export function InstagramSection({ feed, brandData, t }) {
  const posts = (feed?.posts || []).slice(0, 3);
  const username = feed?.profile?.username || 'estadia365';
  const avatarUrl = resolveImageUrl(feed?.profile?.profile_pic_url || brandData?.logo_url || '');
  return (
    <section className="py-20 lg:py-24 bg-[#FAFAF8] dark:bg-[#0F1A2B]" data-testid="instagram-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[minmax(0,280px)_1fr] gap-12 lg:gap-16 items-center">
        <div className="animate-in fade-in slide-in-from-left-4 duration-700">
          <h2 className="text-4xl sm:text-5xl font-bold leading-[1.05] tracking-tight text-[#1C1917] dark:text-white"
            style={{ fontFamily: 'var(--font-heading, Outfit)' }} data-testid="ig-section-title">
            {t('landing.instagram.follow')}<br /><span style={{ color: IG_GREEN }}>Instagram</span>
          </h2>
          <a href={feed?.profile?.url || IG_URL} target="_blank" rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-3 rounded-2xl px-6 py-4 text-white font-semibold shadow-lg shadow-[#2DBE7A]/25 hover:-translate-y-0.5 hover:brightness-110 transition-[transform,filter] duration-200"
            style={{ backgroundColor: IG_GREEN }} data-testid="ig-view-instagram-btn">
            <span className="w-7 h-7 rounded-lg border-2 border-white/90 flex items-center justify-center"><InstagramIcon className="w-4 h-4" /></span>
            {t('landing.instagram.view')}
            <ArrowRight className="w-5 h-5" />
          </a>
        </div>
        {posts.length > 0 && <FeedCarousel posts={posts} username={username} avatarUrl={avatarUrl} />}
      </div>
    </section>
  );
}
